import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Container from '../components/Container';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';
import BadgesRow from '../components/BadgesRow';
import VideoThumbnail from '../components/VideoThumbnail';
import ComponentErrorBoundary from '../components/ComponentErrorBoundary';

// Import utilities
import { getStreamIframeUrl, getStreamThumbnailUrl } from '../lib/cloudflareStream';

// Import content
import teamContent from '../content/team.json';
import siteContent from '../content/site.json';

/**
 * About page component
 * @returns {React.ReactElement} About page
 */
const AboutPage = () => {
  // Get content from JSON files
  const { teamMembers, equipment, companyStory } = teamContent;
  const { badges } = siteContent;

  return (
    <div className="min-h-screen flex flex-col bg-brand-black text-white">
      <Header />
      
      {/* Page Header */}
      <section className="pt-40 md:pt-48 lg:pt-56 pb-12 bg-brand-black text-white">
        <Container>
          <SectionTitle 
            title="About Us" 
            subtitle="Professional drone services with a focus on quality, safety, and customer satisfaction"
            className="mb-0"
          />
        </Container>
      </section>

      {/* Company Story */}
      <section className="py-16 md:py-24 bg-brand-black">
        <Container>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <p className="mb-4">
                {companyStory}
              </p>
            </div>
            <div className="bg-brand-black p-8 flex items-center justify-center">
              <img 
                src="/assets/logo/logo_transparent.svg" 
                alt="Lone Star Aerial Solutions logo" 
                className="w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </Container>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24 bg-brand-black">
        <Container>
          <SectionTitle 
            title="Our Team" 
            subtitle="Meet the professionals behind Lone Star Aerial Solutions"
          />
          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <div key={member.name} className="bg-brand-black border border-gray-700 rounded-2xl shadow-card overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden p-4 flex items-center justify-center" style={{ minHeight: '240px' }}>
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="max-w-full max-h-full w-auto h-auto"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-1">{member.name}</h3>
                  <p className="text-brand-blue-600 font-medium mb-4">{member.role}</p>
                  <p className="text-gray-300">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Equipment Section */}
      <section className="py-16 md:py-24 bg-brand-black">
        <Container>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-card">
              <img 
                src="/assets/team/DJI_Mavic_4_Pro_image.jpg" 
                alt="DJI Mavic 4 Pro drone" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Equipment</h2>
              <p className="mb-4">
                We invest in professional-grade equipment to ensure we deliver the highest quality results 
                for every project. Our fleet includes:
              </p>
              <ul className="space-y-3 mb-6">
                {equipment.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-5 h-5 text-brand-blue-600 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                All our equipment is regularly maintained and updated to ensure reliability and 
                the best possible results for our clients.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Texas Landscapes Showcase */}
      <section className="py-16 md:py-24 bg-brand-black">
        <Container>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Capturing Texas Beauty</h2>
              <p className="mb-4">
                While we specialize in real estate and commercial projects, we're passionate about showcasing 
                the natural beauty of Texas. This cinematic piece demonstrates our commitment to artistic 
                excellence and technical precision in every shot.
              </p>
              <p className="mb-6">
                From rolling hills to dramatic skies, we understand how to capture the essence of the 
                Lone Star State. This same attention to detail and artistic vision goes into every 
                project we deliver for our clients.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-brand-navy/20 border border-brand-blue-600/30 rounded-lg px-4 py-2">
                  <span className="text-brand-blue-600 font-medium">4K Cinematography</span>
                </div>
                <div className="bg-brand-navy/20 border border-brand-blue-600/30 rounded-lg px-4 py-2">
                  <span className="text-brand-blue-600 font-medium">Professional Color Grading</span>
                </div>
                <div className="bg-brand-navy/20 border border-brand-blue-600/30 rounded-lg px-4 py-2">
                  <span className="text-brand-blue-600 font-medium">Smooth Aerial Movements</span>
                </div>
              </div>
            </div>
            <div>
              <ComponentErrorBoundary 
                fallbackMessage="Video is temporarily unavailable."
                componentName="VideoThumbnail"
              >
                <VideoThumbnail
                  thumbnailUrl={getStreamThumbnailUrl('d672935539ebb0f9e5bd9f2e6ea5d782', { 
                    time: '10s', 
                    height: 600 
                  })}
                  videoEmbedUrl={getStreamIframeUrl('d672935539ebb0f9e5bd9f2e6ea5d782', {
                    poster: getStreamThumbnailUrl('d672935539ebb0f9e5bd9f2e6ea5d782', { 
                      time: '10s', 
                      height: 600 
                    })
                  })}
                  title="Texas Landscapes - Cinematic Showcase"
                />
              </ComponentErrorBoundary>
            </div>
          </div>
        </Container>
      </section>

      {/* Certifications Section */}
      <section className="py-16 md:py-24 bg-brand-black">
        <Container>
          <SectionTitle 
            title="Our Certifications" 
            subtitle="We maintain the highest standards of safety and professionalism"
          />
          <div className="max-w-3xl mx-auto">
            <BadgesRow items={badges} className="mb-12" />
            <div className="bg-brand-black border border-gray-700 rounded-2xl shadow-card p-6">
              <h3 className="text-xl font-bold mb-4">What Our Certifications Mean For You</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-brand-blue-600 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong>FAA Part 107 Certified:</strong> All our pilots are certified by the Federal Aviation Administration, 
                    ensuring legal and safe drone operations in compliance with federal regulations.
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-brand-blue-600 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong>Fully Insured:</strong> We carry comprehensive liability insurance, providing peace of mind 
                    for our clients and protection for all parties involved.
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-brand-blue-600 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong>Texas Local:</strong> As a Texas-based company, we understand the unique landscapes, 
                    regulations, and needs of our local clients, providing tailored solutions for the Lone Star State.
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-navy text-white">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Ready to Work With Us?</h2>
            <p className="text-lg mb-8">
              Contact us today to discuss your project and get a customized quote for your aerial needs.
            </p>
            <Button href="/contact">Book a Flight / Get a Quote</Button>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
